import React from "react";
import moment from "moment";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";
import MUIGrid from '@mui/material/Grid';
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIAlert from "@mui/material/Alert";
import MUIAlertTitle from "@mui/material/AlertTitle";
import MUISkeleton from '@mui/material/Skeleton';
import MUIRadioGroup from "@mui/material/RadioGroup";
import MUIRadio from "@mui/material/Radio";
import MUIFormControl from "@mui/material/FormControl";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUISwitch from "@mui/material/Switch";
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import Eligibility from "../Utility/Eligibility";
import MUITooltip from "@mui/material/Tooltip";
import MUIRedoIcon from '@mui/icons-material/Redo';
import MainContext from "../Context/MainContext/MainContext";
import MUIDivider from "@mui/material/Divider";

export default class InsuranceVerification extends React.Component {
    static contextType = MainContext; // For legacy class components

    constructor(props) {
        super(props);

        this.state = {
            hasSecondaryInsurance: this.props.hasSecondaryInsurance,
            insuranceVerifiedRadioValue: "",
            insuranceInNetworkView: true,
            secondaryInsuranceInNetworkView: true,
            verifiedInsurances: this.props.verifiedInsurances || [],
            primaryInsuranceDirty: false,
            secondaryInsuranceDirty: false,
        }
    }

    componentDidMount() {
        this.setVerifiedInsurances();

    }

    componentDidUpdate(prevProps, prevState) {
        const dirtyUpdates = {};
        if (prevProps.insuranceVerification !== this.props.insuranceVerification && this.state.primaryInsuranceDirty) {
            dirtyUpdates.primaryInsuranceDirty = false;
        }
        if (prevProps.secondaryInsuranceVerification !== this.props.secondaryInsuranceVerification && this.state.secondaryInsuranceDirty) {
            dirtyUpdates.secondaryInsuranceDirty = false;
        }
        if (Object.keys(dirtyUpdates).length > 0) {
            this.setState(dirtyUpdates);
        }

        if (prevProps.hasSecondaryInsurance !== this.props.hasSecondaryInsurance) {
            this.setState({
                hasSecondaryInsurance: this.props.hasSecondaryInsurance
            });
        }

        if (prevProps.order?.id !== this.props.order?.id ||
            prevProps.insuranceVerification !== this.props.insuranceVerification ||
            prevProps.secondaryInsuranceVerification !== this.props.secondaryInsuranceVerification ||
            prevState.eligibilitySummaryText !== this.state.eligibilitySummaryText
        ) {
            this.updateEligibilitySummaryText();
            this.setVerifiedInsurances();
            this.props.onUpdateVerifiedInsurances(this.state.verifiedInsurances);
        }

        if (prevState?.verifiedInsurances?.length !== this.state?.verifiedInsurances?.length) {
            this.setVerifiedInsurances();
            this.props.onUpdateVerifiedInsurances(this.state.verifiedInsurances);
        }

        // Force in-network/out-network view based on eligibility results
        const primaryEligibilityResults = this.getEligibility("primary");
        if (primaryEligibilityResults?.detail?.isProviderInNetwork === true && !this.state[`${this.getInsuranceFieldPrefix("primary")}InNetworkView`]) {
            this.setState({ [`${this.getInsuranceFieldPrefix("primary")}InNetworkView`]: true });
        }
        if (primaryEligibilityResults?.detail?.isProviderInNetwork === false && this.state[`${this.getInsuranceFieldPrefix("primary")}InNetworkView`]) {
            this.setState({ [`${this.getInsuranceFieldPrefix("primary")}InNetworkView`]: false });
        }

        const secondaryEligibilityResults = this.getEligibility("secondary");
        if (secondaryEligibilityResults?.detail?.isProviderInNetwork === true && !this.state[`${this.getInsuranceFieldPrefix("secondary")}InNetworkView`]) {
            this.setState({ [`${this.getInsuranceFieldPrefix("secondary")}InNetworkView`]: true });
        }
        if (secondaryEligibilityResults?.detail?.isProviderInNetwork === false && this.state[`${this.getInsuranceFieldPrefix("secondary")}InNetworkView`]) {
            this.setState({ [`${this.getInsuranceFieldPrefix("secondary")}InNetworkView`]: false });
        }
    }

    setVerifiedInsurances() {
        const verifiedInsurances = this.getVerifiedActiveInsurances();
        this.setState({
            verifiedInsurances: verifiedInsurances,
        })
    }

    getInsuranceFieldPrefix(insuranceType) {
        return insuranceType === "primary" ? "insurance" : "secondaryInsurance";
    }

    getEligibility(insuranceType) {
        const order = this.props.order || {};
        if (!order || Object.keys(order).length === 0) {
            return null;
        }

        if (insuranceType === "primary" && this.state.primaryInsuranceDirty) {
            return null;
        }
        if (insuranceType === "secondary" && this.state.secondaryInsuranceDirty) {
            return null;
        }

        const insuranceFieldPrefix = this.getInsuranceFieldPrefix(insuranceType);
        const storedResults = Array.isArray(order.apiResults) ? order.apiResults : [];
        const transientResults = this.props[`${insuranceFieldPrefix}Verification`];

        if (insuranceType === "primary") {
            return Eligibility.getPrimary(storedResults, order, transientResults);
        }

        if (insuranceType === "secondary") {
            return Eligibility.getSecondary(storedResults, order, transientResults);
        }

        return null;
    }



    /**
     * Checks if primary and secondary (if applicable) insurance are active based on eligibility results.
     * Updates state with verified insurance status and results.
     */
    getVerifiedActiveInsurances(forSummaryText) {
        const order = this.props.order || {};
        if (!order || Object.keys(order).length === 0) return;

        const primaryEligibilityResult = this.getEligibility("primary");
        const secondaryEligibilityResult = this.state.hasSecondaryInsurance
            ? this.getEligibility("secondary")
            : null;

        const primaryActive = primaryEligibilityResult?.eligible === true;
        const secondaryActive = secondaryEligibilityResult?.eligible === true;

        const shouldSet = forSummaryText || (
            primaryActive && (!this.state.hasSecondaryInsurance || secondaryActive)
        );

        if (!shouldSet) return [];

        const verifiedInsurances = [];

        if (primaryActive) {
            verifiedInsurances.push({
                type: "primary",
                results: primaryEligibilityResult
            });
        }

        if (this.state.hasSecondaryInsurance && secondaryActive) {
            verifiedInsurances.push({
                type: "secondary",
                results: secondaryEligibilityResult
            });
        }

        return verifiedInsurances;
    }

    /**
     * Generates a detailed eligibility summary text based on verified insurances.
     * This version provides more comprehensive coverage details.
     */
    updateEligibilitySummaryText() {
        const verifiedInsurances = this.getVerifiedActiveInsurances(true);

        if (!verifiedInsurances || verifiedInsurances.length === 0) {
            return;
        }

        let summaryText = '';
        const formatCurrency = (amount) => {
            return amount === null ? 'unknown' :
                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
        };

        if (verifiedInsurances && verifiedInsurances.length === 1) {
            const insurance = verifiedInsurances[0]?.results?.detail;
            const deductible = insurance?.isMedicare ? insurance?.deductible?.partB : insurance?.deductible?.inNetwork;
            const oop = insurance?.outOfPocket?.inNetwork;
            const coinsurance = insurance?.dmeSummary?.coInsuranceInNetwork;

            if (deductible === 0 && oop === 0) {
                summaryText = 'Deductible and out-of-pocket maximum met. If in network, all covered services estimated to be paid at 100%.';
            } else {
                let deductibleText = "Deductible remaining unknown";
                if (deductible === 0) {
                    deductibleText = "Deductible met";
                } else if (deductible !== null) {
                    deductibleText = `${formatCurrency(deductible)} left on deductible`;
                }

                let oopText = "and out-of-pocket remaining unknown";
                if (oop === 0) {
                    oopText = "and out-of-pocket met";
                } else if (oop !== null) {
                    oopText = `and ${formatCurrency(oop)} before reaching out‑of‑pocket maximum`;
                }

                let responsibilityText = deductible === 0
                    ? "If in network, patient estimated to be responsible for"
                    : "If in network, after deductible is met, patient estimated to be responsible for";

                let coinsuranceText = coinsurance || "an unknown percentage";

                if (deductible === null && oop === 0) {
                    summaryText = `${deductibleText} ${oopText}. If in network, all covered services estimated to be paid at 100%.`;
                } else {
                    summaryText = `${deductibleText} ${oopText}. ${responsibilityText} ${coinsuranceText} of remaining costs.`;
                }
            }
        } else {
            const primary = verifiedInsurances[0]?.results?.detail;
            const secondary = verifiedInsurances[1]?.results?.detail;

            const primaryDeductible = primary?.isMedicare ? primary?.deductible?.partB : primary?.deductible?.inNetwork;
            const secondaryDeductible = secondary?.isMedicare ? secondary?.deductible?.partB : secondary?.deductible?.inNetwork;
            const primaryOOP = primary?.outOfPocket?.inNetwork;
            const secondaryOOP = secondary?.outOfPocket?.inNetwork;
            const maxOOP = primaryOOP === null || secondaryOOP === null ? null : Math.max(primaryOOP || 0, secondaryOOP || 0);

            const primaryCoinsurance = primary?.dmeSummary?.coInsuranceInNetwork;
            const secondaryCoinsurance = secondary?.dmeSummary?.coInsuranceInNetwork;
            let cumulativeCoinsurance = 'an unknown percentage';

            if (primaryCoinsurance && secondaryCoinsurance) {
                const primaryRate = parseFloat(primaryCoinsurance) / 100;
                const secondaryRate = parseFloat(secondaryCoinsurance) / 100;
                cumulativeCoinsurance = Math.round(primaryRate * secondaryRate * 100);
            }
            if (primaryDeductible === 0 && secondaryDeductible === 0 && primaryOOP === 0 && secondaryOOP === 0) {
                summaryText = 'Deductibles and out-of-pocket maximums met for both insurances. If in network, all covered services estimated to be paid at 100%.';
            } else if (primaryDeductible === 0 && secondaryDeductible === 0 && primaryCoinsurance === '0%' && secondaryCoinsurance === '0%') {
                summaryText = 'Deductibles met for both insurances and coinsurance is 0%. If in network, all covered services estimated to be paid at 100%.';
            } else {
                let deductibleMessage = "";
                if (primaryDeductible === 0 && secondaryDeductible === 0) {
                    deductibleMessage = "primary and secondary deductibles met";
                } else {
                    let primaryMessage = "";
                    if (primaryDeductible === null) {
                        primaryMessage = "primary deductible remaining unknown";
                    } else if (primaryDeductible === 0) {
                        primaryMessage = "primary deductible met";
                    } else {
                        primaryMessage = `${formatCurrency(primaryDeductible)} left on primary deductible`;
                    }

                    let secondaryMessage = "";
                    if (secondaryDeductible === null) {
                        secondaryMessage = "secondary deductible remaining unknown";
                    } else if (secondaryDeductible === 0) {
                        secondaryMessage = "secondary deductible met";
                    } else {
                        secondaryMessage = `${formatCurrency(secondaryDeductible)} on secondary`;
                    }

                    deductibleMessage = `${primaryMessage} and ${secondaryMessage}`;
                }

                let oopMessage = "";
                if (maxOOP === null) {
                    oopMessage = "Out-of-pocket remaining unknown";
                } else {
                    const whichInsurance = primaryOOP === maxOOP ? 'Primary' : 'Secondary';
                    oopMessage = `${whichInsurance} out-of-pocket remaining is ${formatCurrency(maxOOP)}`;
                }

                const coinsuranceIntro = primaryDeductible === 0 && secondaryDeductible === 0
                    ? 'If in network, after insurance processes, patient estimated to be responsible for'
                    : 'If in network, after deductibles are met and insurance processes, patient estimated to be responsible for';

                const coinsuranceAmount = cumulativeCoinsurance === 'an unknown percentage'
                    ? 'an unknown percentage'
                    : `${cumulativeCoinsurance}%`;

                if (primaryDeductible === null && secondaryDeductible === null && primaryOOP === 0 && secondaryOOP === 0) {
                    summaryText = `If in network, ${deductibleMessage}. ${oopMessage}. If in network, all covered services estimated to be paid at 100%.`;
                } else {
                    summaryText = `If in network, ${deductibleMessage}. ${oopMessage}. ${coinsuranceIntro} ${coinsuranceAmount} of remaining costs.`;
                }
            }
        }

        this.setState({
            eligibilitySummaryText: summaryText
        });

        this.props.onChange({
            ...this.props.order,
            eligibilitySummaryText: summaryText
        });
    }

    /**
     * Checks if the Check Eligibility button should be disabled based on verified insurances
     * and matching data on the following fields: DOB, First Name, Last Name, Payor Source, Policy #, and Group #.
     * Returns true if the button should be disabled, false if it should be enabled.
     *
     * @param {string} insuranceType - Insurance type ("primary"|"secondary")
     * @returns {boolean} - True if button should be disabled, false if enabled
     */
    shouldDisableEligibilityButton(insuranceType) {
        const order = this.props.order || {};
        const insuranceFieldPrefix = this.getInsuranceFieldPrefix(insuranceType);
        const verifiedInsurances = this.getVerifiedActiveInsurances(true);

        const verification = verifiedInsurances.find(insurance => insurance.type === insuranceType)?.results;
        if (verification) {
            const insuranceDOB = verification.detail.noteData.DemographicInfo?.Subscriber?.DOB_R ?
                moment(verification.detail.noteData.DemographicInfo?.Subscriber?.DOB_R).format("MM/DD/YYYY")
                : null;
            const insuranceFirstName = verification.detail.noteData.DemographicInfo?.Subscriber?.Firstname;
            const insuranceLastName = verification.detail.noteData.DemographicInfo?.Subscriber?.Lastname_R;
            const insurancePverifyPayerCode = verification.detail.pvPayerCode;
            const insurancePolicyId = verification.detail.policyId;

            const matchingFields = insuranceDOB === moment(order.patient.dateOfBirth).format("MM/DD/YYYY") &&
                insuranceFirstName.toLowerCase().trim() === order.patient.firstName.toLowerCase().trim() &&
                insuranceLastName.toLowerCase().trim() === order.patient.lastName.toLowerCase().trim() &&
                insurancePverifyPayerCode === this.context.insuranceSubTypesIndexed[order[`${insuranceFieldPrefix}SubTypeId`]]?.pvPayorId &&
                insurancePolicyId === order[`${insuranceFieldPrefix}Policy`];

            const verificationDate = moment(verification.detail.createDate);
            const currentDate = moment();

            const isSameMonth = verificationDate.month() === currentDate.month() && verificationDate.year() === currentDate.year();

            if (matchingFields && isSameMonth) {
                return true;
            }

            return false;
        }


        return false;
    }

    renderPatientDemographics() {
        return (
            <MUIBox>
                <MUITypography variant="subtitle2" sx={{ marginBottom: 2 }}>
                    Demographics
                </MUITypography>
                <MUIGrid container spacing={1} alignItems="center">
                    <MUIGrid item xs={12} sm={6} md={4}>
                        <MUITextField
                            value={this.props.order?.patient ? this.props.order.patient.firstName : ""}
                            fullWidth={true}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { resize: "both" } }}
                            label={"First Name"}
                            onChange={(e) => this.props.onChange({
                                ...this.props.order,
                                patient: {
                                    ...this.props.order.patient,
                                    firstName: e.target.value
                                }
                            })}
                            variant="standard"
                        />
                    </MUIGrid>
                    <MUIGrid item xs={12} sm={6} md={4}>
                        <MUITextField
                            value={this.props.order?.patient ? this.props.order.patient.lastName : ""}
                            fullWidth={true}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { resize: "both" } }}
                            label={"Last Name"}
                            onChange={(e) => this.props.onChange({
                                ...this.props.order,
                                patient: {
                                    ...this.props.order.patient,
                                    lastName: e.target.value
                                }
                            })}
                            variant="standard"
                        />
                    </MUIGrid>
                    <MUIGrid item xs={12} sm={6} md={4}>
                        <MUIDatePicker
                            label="DOB"
                            value={this.props.order?.patient?.dateOfBirth ? moment(this.props.order.patient.dateOfBirth) : null}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                    variant: 'standard',
                                    InputLabelProps: {
                                        shrink: true
                                    },
                                    error: false,
                                },
                            }}
                            onChange={(value) => this.props.onChange({
                                ...this.props.order,
                                patient: {
                                    ...this.props.order.patient,
                                    dateOfBirth: value ? moment(value).format('M/D/YYYY') : null
                                }
                            })}
                            views={['year', 'month', 'day']}
                        />
                    </MUIGrid>
                </MUIGrid>
            </MUIBox>
        )
    }

    /**
     * Renders the insurance verification section for primary or secondary insurance.
     * Handles insurance type and subtype selection, policy and group number input, and eligibility check.
     * Displays eligibility results and validation issues.
     *
     * @param {string} label - Label for the insurance type (Primary or Secondary)
     * @param {string} insuranceType - Insurance type ("primary"|"secondary")
     */
    renderInsuranceVerification(label, insuranceType) {
        const order = this.props.order || {};
        const fieldPrefix = this.getInsuranceFieldPrefix(insuranceType);

        const getInsuranceTypeOptions = () =>
            Object.values(this.context.insuranceTypesIndexed).map((option) => ({
                title: option.name,
                value: option.id,
            }));

        const getInsuranceSubTypeOptions = () => {
            const selectedTypeId = order[`${fieldPrefix}TypeId`];
            return Object.values(this.context.insuranceSubTypesIndexed)
                .filter(subType => subType.insuranceTypeId === selectedTypeId)
                .map((option) => ({
                    title: option.name,
                    value: option.id,
                }));
        };

        const typeIdKey = `${fieldPrefix}TypeId`;
        const subTypeIdKey = `${fieldPrefix}SubTypeId`;
        const policyKey = `${fieldPrefix}Policy`;
        const groupKey = `${fieldPrefix}Group`;

        const selectedType = getInsuranceTypeOptions().find(option => option.value === order[typeIdKey]) || null;
        const selectedSubType = getInsuranceSubTypeOptions().find(option => option.value === order[subTypeIdKey]) || null;

        const checkSubTypePayorId = this.context.insuranceSubTypesIndexed[selectedSubType?.value]?.pvPayorId;

        return (
            <MUIBox sx={{ mt: 4 }}>
                <MUIGrid container spacing={2} alignItems="center" wrap="nowrap" sx={{ mb: 2 }}>
                    <MUIGrid item>
                        <MUITypography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                            {label}
                        </MUITypography>
                    </MUIGrid>

                    {insuranceType === "secondary" && (
                        <MUIGrid item>
                            <MUIFormControlLabel
                                control={
                                    <MUISwitch
                                        size="small"
                                        onChange={(e) => {
                                            const hasSecondary = e.target.checked;
                                            this.setState({ hasSecondaryInsurance: hasSecondary });
                                            this.props.onChange({
                                                ...this.props.order,
                                                hasSecondaryInsurance: hasSecondary
                                            });
                                        }}
                                        checked={Boolean(this.state.hasSecondaryInsurance)}
                                    />
                                }
                                labelPlacement="end"
                                sx={{ m: 0 }}
                            />
                        </MUIGrid>
                    )}
                </MUIGrid>

                {insuranceType === "secondary" && !this.state.hasSecondaryInsurance ? null :
                    (
                        <>
                            <MUIGrid container spacing={1} alignItems="center">
                                <MUIGrid item xs={12} sm={6} md={3}>
                                    <MUIAutocomplete
                                        key={`${fieldPrefix}-subtype-autocomplete`}
                                        options={getInsuranceTypeOptions()}
                                        value={selectedType}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        getOptionLabel={(option) => option.title}
                                        onChange={(e, newValue) => {
                                            const subTypeOptions = Object.values(this.context.insuranceSubTypesIndexed)
                                                .filter(subType => subType.insuranceTypeId === (newValue?.value || null));

                                            const updates = {
                                                [typeIdKey]: newValue ? newValue.value : null,
                                                [subTypeIdKey]: null
                                            };

                                            if (subTypeOptions.length === 1) {
                                                updates[subTypeIdKey] = subTypeOptions[0].id;
                                            }

                                            this.setState({
                                                ...(insuranceType === "primary"
                                                    ? { primaryInsuranceDirty: true }
                                                    : { secondaryInsuranceDirty: true })
                                            });
                                            this.props.onChange({
                                                ...order,
                                                ...updates
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <MUITextField
                                                {...params}
                                                InputLabelProps={{ shrink: true }}
                                                size="small"
                                                variant="standard"
                                                label="Payor Source"
                                                fullWidth
                                                error={this.props.validationIssues?.[typeIdKey] ? true : false}
                                                helperText={this.props.validationIssues ? this.props.validationIssues[typeIdKey] : ""}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={`${option.value}-${fieldPrefix}`}>
                                                {option.title}
                                            </li>
                                        )}
                                    />
                                </MUIGrid>

                                <MUIGrid item xs={12} sm={6} md={3}>
                                    <MUIAutocomplete
                                        key={`${fieldPrefix}-type-autocomplete`}
                                        options={getInsuranceSubTypeOptions()}
                                        value={selectedSubType}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        getOptionLabel={(option) => option.title}
                                        onChange={(e, newValue) => {
                                            this.setState({
                                                ...(insuranceType === "primary"
                                                    ? { primaryInsuranceDirty: true }
                                                    : { secondaryInsuranceDirty: true })
                                            });
                                            this.props.onChange({
                                                ...order,
                                                [subTypeIdKey]: newValue ? newValue.value : null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <MUITextField
                                                {...params}
                                                InputLabelProps={{ shrink: true }}
                                                size="small"
                                                variant="standard"
                                                label="Insurance"
                                                fullWidth
                                                error={this.props.validationIssues?.[subTypeIdKey] ? true : false}
                                                helperText={this.props.validationIssues ? this.props.validationIssues[subTypeIdKey] : ""}
                                            />
                                        )
                                        }
                                        renderOption={(props, option) => (
                                            <li {...props} key={`${option.value}-${fieldPrefix}`}>
                                                {option.title}
                                            </li>
                                        )}
                                    />
                                </MUIGrid>

                                <MUIGrid item xs={12} sm={6} md={3}>
                                    <MUITextField
                                        value={order[policyKey] || ""}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            maxLength: order[typeIdKey] === 1 ? 11 : undefined
                                        }}
                                        label={order[typeIdKey] === 1 ? "MBI" : "Policy #"}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            if (order[typeIdKey] === 1) {
                                                // If MBI field, only allow letters and numbers
                                                value = value.replace(/[^a-zA-Z0-9]/g, '');
                                            }
                                            this.setState({
                                                ...(insuranceType === "primary"
                                                    ? { primaryInsuranceDirty: true }
                                                    : { secondaryInsuranceDirty: true })
                                            });
                                            this.props.onChange({
                                                ...order,
                                                [policyKey]: value.trim(),
                                            });
                                        }}
                                        variant="standard"
                                        error={this.props.validationIssues?.[policyKey] ? true : false}
                                        helperText={this.props.validationIssues ? this.props.validationIssues[policyKey] : ""}
                                    />
                                </MUIGrid>

                                <MUIGrid item xs={12} sm={6} md={3}>
                                    <MUITextField
                                        value={order[groupKey] || ""}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ style: { resize: "both" } }}
                                        label="Group #"
                                        onChange={(e) => {
                                            this.props.onChange({
                                                ...order,
                                                [groupKey]: e.target.value.trim(),
                                            });
                                        }}
                                        variant="standard"
                                    />
                                </MUIGrid>
                            </MUIGrid>

                            <MUIGrid container spacing={1} alignItems="top">
                                <MUIGrid item xs={12} sm={12} md={3}>
                                    <MUILoadingButton
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        sx={{ mt: 1 }}
                                        onClick={() => {
                                            this.props.onCheckEligibility(order, fieldPrefix);
                                            if (this.state.insuranceVerifiedRadioValue !== null || this.state.insuranceVerifiedRadioValue !== "") {
                                                this.setState({
                                                    insuranceVerifiedRadioValue: ""
                                                });
                                                const { verifiedInsurance, ...orderWithoutVerifiedInsurance } = this.props.order;
                                                this.props.onChange({
                                                    ...orderWithoutVerifiedInsurance,
                                                    shouldCreateVerificationNote: null,
                                                    insuranceVerified: null,
                                                });
                                            }
                                        }}
                                        disabled={selectedSubType && !checkSubTypePayorId || this.shouldDisableEligibilityButton(insuranceType)}
                                        loading={this.props[`${fieldPrefix}Loading`]}
                                    >
                                        Check Eligibility
                                    </MUILoadingButton>
                                </MUIGrid>

                                {selectedSubType && !checkSubTypePayorId ? (
                                    <MUIGrid item xs={12} sm={12} md={9}>
                                        <MUIAlert severity="warning" sx={{ mt: 1, width: '100%' }}>
                                            <MUITypography variant="subtitle2">
                                                This payor is not configured for electronic eligibility. Please send to Business Support.
                                            </MUITypography>
                                        </MUIAlert>
                                    </MUIGrid>
                                ) : (
                                    <>
                                        {this.props[`${fieldPrefix}Loading`] ? (
                                            <MUIGrid item xs={12} sm={6} md={9}>
                                                <MUISkeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
                                            </MUIGrid>
                                        ) : (
                                            <>
                                                <MUIGrid item xs={12} sm={12} md={9}>
                                                    {this.renderEligibilityResultsAlert(insuranceType)}
                                                </MUIGrid>
                                            </>
                                        )}
                                    </>
                                )}
                            </MUIGrid>
                        </>
                    )}
            </MUIBox>
        );
    }

    renderSameSimVerification() {
        const order = this.props.order || {};
        if (!order || order.insuranceTypeId !== 1) return null; // Only show for Medicare

        const apiResults = this.props.sameSimResults || order.apiResults;
        const result = Eligibility.getSameSim(apiResults);

        const hasChecked =
            result.status === "processed" ||
            result.status === "error" ||
            result.status === "pending" ||
            result.status === "warning";
        const disableApiStatus = hasChecked && result.status !== "error";

        const mbi = (order.insurancePolicy || "").toUpperCase();
        let mbiError = null;

        if (!disableApiStatus) {
            if (mbi.length !== 11) {
                mbiError = "MBI must be exactly 11 characters.";
            } else if (/[SLOIBZ]/.test(mbi)) {
                mbiError = "MBI can't contain S, L, O, I, B, or Z.";
            } else if (!/^[1-9][A-Z][0-9A-Z]\d[A-Z][0-9A-Z]\d[A-Z]{2}\d{2}$/.test(mbi)) {
                mbiError = "Invalid MBI format.";
            }
        }

        const disableButton = disableApiStatus || !!mbiError;

        let severity = null;
        let message = null;

        if (result.status === "pending") {
            severity = "info";

            const etaSeconds = result.detail?.expectedTimeInSeconds;
            let etaMessage = "Pending";
            if (typeof etaSeconds === "number") {
                if (etaSeconds < 60) {
                    etaMessage = "Expected in under a minute";
                } else if (etaSeconds < 90) {
                    etaMessage = "Expected in about a minute";
                } else if (etaSeconds < 150) {
                    etaMessage = "Expected in about two minutes";
                } else if (etaSeconds < 3600) {
                    etaMessage = `Expected in about ${Math.round(etaSeconds / 60)} minutes`;
                } else {
                    etaMessage = "Expected in an hour or longer";
                }
            }

            message = etaMessage;
        } else if (result.status === "error") {
            severity = "error";
            message = result.detail?.errorMessage || "Error checking Same/Sim";
        } else if (result.status === "warning") {
            severity = "warning";
            const claims = result.detail?.claims || [];
            if (claims.length > 0) {
                const list = claims
                    .map(c => `${c.hcpcsCode} on ${moment(c.dosStart).format("MM/DD/YYYY")}`)
                    .join(", ");
                message = `Wheelchair claim on file in last 5 years (${list})`;
            } else {
                message = "Wheelchair claim on file in last 5 years";
            }
        } else if (result.status === "processed") {
            if (result.eligible === false) {
                severity = "error";
                message = "Same/Sim on file";
            } else if (result.eligible === true) {
                severity = "success";
                message = "No Same/Sim on file";
            }
        }

        return (
            <MUIBox sx={{ mt: 4 }}>
                <MUIGrid container spacing={2} alignItems="center" wrap="nowrap" sx={{ mb: 1 }}>
                    <MUIGrid item>
                        <MUITypography variant="subtitle2">
                            Same/Sim
                        </MUITypography>
                    </MUIGrid>
                </MUIGrid>

                <MUIGrid container spacing={1} alignItems="top">
                    <MUIGrid item xs={12} sm={12} md={3}>
                        <MUITooltip title={mbiError ? mbiError : disableButton ? "Check for Same/Sim conflicts" : ""}>
                            <span>
                                <MUILoadingButton
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    onClick={() => this.props.onCheckSameSim(order)}
                                    loading={this.props.sameSimVerificationLoading}
                                    disabled={disableButton}
                                >
                                    Check Same/Sim
                                </MUILoadingButton>
                            </span>
                        </MUITooltip>
                    </MUIGrid>

                    <MUIGrid item xs={12} sm={12} md={9}>
                        {this.props.sameSimVerificationLoading ? (
                            <MUISkeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
                        ) : (
                            severity && (
                                <MUIAlert severity={severity} sx={{ mt: 1, width: '100%' }}>
                                    <MUITypography variant="subtitle2">{message}</MUITypography>
                                </MUIAlert>
                            )
                        )}
                    </MUIGrid>
                </MUIGrid>
            </MUIBox>
        );
    }

    /**
     * Renders an alert based on eligibility results.
     * Displays error description if present, otherwise shows plan name, status, and dates.
     *
     * @param {string} insuranceType - Insurance type ("primary"|"secondary")
     */
    renderEligibilityResultsAlert(insuranceType) {
        const fieldPrefix = this.getInsuranceFieldPrefix(insuranceType);
        const eligibilityResults = this.getEligibility(insuranceType);

        if (eligibilityResults) {
            if (eligibilityResults?.status === "unknown") {
                // Unknown = no results yet, don't show anything
                return null;
            } else if (eligibilityResults?.status === "error") {
                return (
                    // <MUIGrid item xs={12} sm={6} md={9}>
                    <MUIAlert severity="error" sx={{ mt: 1, width: '100%' }}>
                        <MUITypography variant="subtitle2">
                            {eligibilityResults?.detail?.errorDescription || "Error checking eligibility"}
                        </MUITypography>
                    </MUIAlert>
                    // </MUIGrid>
                );
            } else {
                const additionalInfo = eligibilityResults?.detail?.additionalInfo;
                const payerInfo = Array.isArray(additionalInfo?.PAYER) ? additionalInfo.PAYER.join("\n") : "";
                const verificationInfo = Array.isArray(additionalInfo?.VERIFICATION) && additionalInfo.VERIFICATION.length > 0
                    ? additionalInfo.VERIFICATION.join("\n")
                    : null;
                const verificationInfoSeverity = verificationInfo && /send to verifications/i.test(verificationInfo)
                    ? "warning"
                    : "info";

                // Determine severity for main alert
                let severity = "info";
                if (eligibilityResults?.eligible === false || eligibilityResults?.detail?.isProviderInNetwork === false) {
                    severity = "error";
                } else if (eligibilityResults?.eligible === true && eligibilityResults?.detail?.isProviderInNetwork === true) {
                    severity = "success";
                } else if (eligibilityResults?.eligible === true && eligibilityResults?.detail?.isProviderInNetwork == null) {
                    severity = "warning";
                }

                return (
                    <>
                        <MUIAlert
                            severity={severity}
                            sx={{ mt: 1, width: '100%' }}
                        >
                            <>
                                {eligibilityResults?.eligible === false || eligibilityResults?.eligible === null ? (
                                    <>
                                        <MUITypography variant="subtitle2">
                                            Status: {eligibilityResults?.eligible === false ? "Inactive" : "Unknown"}
                                        </MUITypography>
                                        <MUITypography variant="subtitle2">
                                            Date Checked: {eligibilityResults?.detail?.createDate || "Unknown"}
                                            {eligibilityResults?.detail?.isSkipped === true && (
                                                <MUITooltip title="pVerify skipped check and returned previous result">
                                                    <MUIRedoIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 1 }} />
                                                </MUITooltip>
                                            )}
                                        </MUITypography>
                                        {payerInfo ? (
                                            <>
                                                <MUIDivider sx={{ my: 1 }} />
                                                <MUITypography variant="body2">
                                                    {payerInfo}
                                                </MUITypography>
                                            </>
                                        ) : null}
                                    </>
                                ) : (
                                    <>
                                        <MUITooltip title={eligibilityResults?.detail?.planName || "N/A"}>
                                            <MUIAlertTitle>
                                                Plan:{(eligibilityResults?.detail?.planName || "").length > 100
                                                    ? `${(eligibilityResults?.detail?.planName || "").slice(0, 100)}...`
                                                    : (eligibilityResults?.detail?.planName || "N/A")}
                                            </MUIAlertTitle>
                                        </MUITooltip>
                                        {eligibilityResults?.detail?.payerName && !eligibilityResults?.detail?.isMedicare ? (
                                            <>
                                                <MUITypography variant="subtitle2">
                                                    Payer: {eligibilityResults?.detail?.payerName}
                                                </MUITypography>
                                            </>
                                        ) : null}
                                        {eligibilityResults?.detail?.policyType ? (
                                            <>
                                                <MUITypography variant="subtitle2">
                                                    Type: {eligibilityResults.detail.policyType}
                                                </MUITypography>
                                            </>
                                        ) : null}
                                        <MUIDivider sx={{ my: 1 }} />
                                        <MUIGrid container spacing={6} sx={{ width: '100%', margin: 0 }}>
                                            <MUIGrid item xs="auto" sx={{ paddingLeft: "0 !important", paddingTop: "0 !important" }}>
                                                <MUITypography variant="subtitle2">
                                                    Status: Active
                                                </MUITypography>
                                                <MUITypography variant="subtitle2">
                                                    Date Checked: {eligibilityResults?.detail?.createDate || "Unknown"}
                                                    {eligibilityResults?.detail?.isSkipped === true && (
                                                        <MUITooltip title="pVerify skipped check and returned previous result">
                                                            <MUIRedoIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 1 }} />
                                                        </MUITooltip>
                                                    )}
                                                </MUITypography>
                                            </MUIGrid>
                                            <MUIGrid item xs="auto" sx={{ paddingTop: "0 !important" }}>
                                                <MUITypography variant="subtitle2">
                                                    {`${eligibilityResults?.detail?.isMedicare ? 'Part B Deductible Remaining' : 'Deductible Remaining'}: ${eligibilityResults?.detail?.isMedicare ?
                                                        (eligibilityResults?.detail?.deductible?.partB === null ? 'N/A' :
                                                            new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(eligibilityResults?.detail?.deductible?.partB)) :
                                                        (this.state[`${fieldPrefix}InNetworkView`] ?
                                                            (eligibilityResults?.detail?.deductible?.inNetwork === null ? 'N/A' :
                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(eligibilityResults?.detail?.deductible?.inNetwork)) :
                                                            (eligibilityResults?.detail?.deductible?.outNetwork === null ? 'N/A' :
                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(eligibilityResults?.detail?.deductible?.outNetwork)))}`}
                                                </MUITypography>
                                                <MUITypography variant="subtitle2">
                                                    Coinsurance: {this.state[`${fieldPrefix}InNetworkView`] ?
                                                        (eligibilityResults?.detail?.dmeSummary?.coInsuranceInNetwork || "N/A") :
                                                        (eligibilityResults?.detail?.dmeSummary?.coInsuranceOutNetwork || "N/A")}
                                                </MUITypography>
                                                <MUITypography variant="subtitle2">
                                                    OOP Remaining: {this.state[`${fieldPrefix}InNetworkView`] ?
                                                        (eligibilityResults?.detail?.outOfPocket?.inNetwork === null ? 'N/A' :
                                                            new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(eligibilityResults?.detail?.outOfPocket?.inNetwork)) :
                                                        (eligibilityResults?.detail?.outOfPocket?.outNetwork === null ? 'N/A' :
                                                            new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(eligibilityResults?.detail?.outOfPocket?.outNetwork))}
                                                </MUITypography>
                                            </MUIGrid>
                                        </MUIGrid>
                                        <MUIGrid container spacing={1} sx={{ width: '100%', margin: 0 }}>
                                            <MUITypography variant="body2" sx={{ display: 'flex', alignItems: 'center', mt: 1, fontStyle: 'italic' }}>
                                                {(() => {
                                                    if (eligibilityResults?.detail?.isProviderInNetwork === true) {
                                                        // Only display text, do not setState here
                                                        return "In Network • Showing In Network Benefits";
                                                    }
                                                    if (eligibilityResults?.detail?.isProviderInNetwork === false) {
                                                        // Only display text, do not setState here
                                                        return "Out of Network • Showing Out of Network Benefits";
                                                    }
                                                    // Unknown network status, keep switch
                                                    return (
                                                        <>
                                                            Network Status Unknown
                                                            {` • Showing ${this.state[`${fieldPrefix}InNetworkView`] ? "In Network" : "Out of Network"} Benefits (`}
                                                            <span
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: (theme) => theme.palette.info.light,
                                                                    textDecoration: 'underline',
                                                                }}
                                                                onClick={() => this.setState({ [`${fieldPrefix}InNetworkView`]: !this.state[`${fieldPrefix}InNetworkView`] })}>
                                                                Switch
                                                            </span>
                                                            {`)`}
                                                        </>
                                                    );
                                                })()}
                                            </MUITypography>
                                        </MUIGrid>
                                        {/* Additional Info Section */}
                                        {payerInfo ? (
                                            <>
                                                <MUIDivider sx={{ my: 1 }} />
                                                <MUITypography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                    {payerInfo}
                                                </MUITypography>
                                            </>
                                        ) : null}
                                    </>
                                )}
                            </>
                        </MUIAlert>
                        {verificationInfo && (
                            <MUIAlert severity={verificationInfoSeverity} sx={{ mt: 1, width: '100%' }}>
                                <MUITypography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {verificationInfo}
                                </MUITypography>
                            </MUIAlert>
                        )}
                    </>
                );
            }
        }
    }

    renderEligibilitySummaryAlert() {
        const verifiedInsurances = this.getVerifiedActiveInsurances(true);

        if (verifiedInsurances?.length === 0) {
            return null;
        }

        if (!this.state.eligibilitySummaryText && !this.props.order?.eligibilitySummaryText) {
            return null;
        }

        return (
            <MUIGrid container spacing={1} sx={{ mt: 3 }}>
                <MUIGrid item>
                    <MUITypography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                        Eligibility Summary
                    </MUITypography>
                </MUIGrid>
                <MUIGrid item xs={12}>
                    <MUIAlert severity={"info"} sx={{ mt: 1, width: '100%' }}>
                        <MUITypography variant="subtitle2">
                            {this.state.eligibilitySummaryText || this.props.order?.eligibilitySummaryText || null}
                        </MUITypography>
                    </MUIAlert>
                </MUIGrid>
                <MUIGrid item xs={12}>
                    <MUITypography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem', mt: 1 }}>
                        Summary is based on in network benefits. Actual benefits may be affected by network status, plan type, equipment to be delivered, pending claims, or coordination of benefits between primary/secondary.
                    </MUITypography>
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     * Renders the main insurance verification component.
     * Includes patient demographics, insurance verification sections, and verification status.
     */
    render() {
        const verifiedInsurances = this.getVerifiedActiveInsurances();
        return (
            <MUIBox sx={{ mt: 2 }}>
                {this.renderPatientDemographics()}
                {this.props.order && this.renderInsuranceVerification("Primary", "primary")}
                {this.props.order && this.renderInsuranceVerification("Secondary", "secondary")}
                {/* {this.renderSameSimVerification()} */}
                {this.renderEligibilitySummaryAlert()}
                {verifiedInsurances?.length > 0 ? (
                    <MUIGrid container sx={{ mt: 3 }}>
                        <MUIGrid item xs={12}>
                            <MUIFormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <MUITypography variant="subtitle1" sx={{ mr: 2 }}>
                                    Insurance Verified?
                                </MUITypography>
                                <MUIRadioGroup
                                    sx={{ mt: '5px' }}
                                    row
                                    value={this.props.order.verifiedInsurance && this.props.order.verifiedInsurance.length > 0 ?
                                        (this.props.order.insuranceVerified ? "yes" : "no") :
                                        (this.state.insuranceVerifiedRadioValue === null || this.state.insuranceVerifiedRadioValue === "" ? "" :
                                            (this.state.insuranceVerifiedRadioValue ? "yes" : "no"))}
                                    onChange={(e) => {
                                        const isVerified = e.target.value === "yes";
                                        this.setState({ insuranceVerifiedRadioValue: isVerified });
                                        this.props.onChange({
                                            ...this.props.order,
                                            shouldCreateVerificationNote: isVerified ? true : false,
                                            verifiedInsurance: isVerified ? verifiedInsurances : [],
                                            insuranceVerified: isVerified ? true : false,
                                        });
                                    }}
                                >
                                    <MUIFormControlLabel
                                        value="yes"
                                        control={<MUIRadio size="small" />}
                                        label="Yes"
                                    />
                                    <MUIFormControlLabel
                                        value="no"
                                        control={<MUIRadio size="small" />}
                                        label="No"
                                    />
                                </MUIRadioGroup>
                            </MUIFormControl>
                        </MUIGrid>
                    </MUIGrid>
                ) : null}
            </MUIBox>
        )
    }
}
